import { Component, inject, signal, ChangeDetectionStrategy, OnInit, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, catchError, of, from, tap } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AccountService } from '../../../core/services/account.service';
import { AuthService } from '../../../core/auth/auth.service';
import { OrganizationManageService } from '../../../core/services/organization-manage.service';
import { SiteManagementDepartmentService } from '../../../core/services/site-management-department.service';
import { SiteManagementDepartment } from '../../../shared/interfaces';
import type { UserRole } from '../../../core/auth/role.model';

@Component({
  selector: 'app-account-create',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 max-w-2xl mx-auto">
      <div class="flex items-center mb-6">
        <button matIconButton aria-label="戻る" routerLink="/accounts/list" class="mr-4">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <h1 class="text-2xl font-bold">ユーザー追加</h1>
      </div>

      <div class="bg-primary-container rounded-lg shadow-sm p-6">
        <form [formGroup]="form" (ngSubmit)="onSubmit()">
          
          <div class="mb-6">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>権限（ロール）</mat-label>
              <mat-select formControlName="role">
                @if (authService.userRole() === 'super_admin') {
                  <mat-option value="super_admin">スーパー管理者 (super_admin)</mat-option>
                }
                <mat-option value="org_admin">テナント管理者</mat-option>
                <mat-option value="dep_admin">部門管理者</mat-option>
              </mat-select>
              @if (form.get('role')?.hasError('required') && form.get('role')?.touched) {
                <mat-error>ロールを選択してください</mat-error>
              }
            </mat-form-field>
          </div>

          @if (authService.userRole() === 'super_admin' && form.get('role')?.value !== 'super_admin') {
            <div class="mb-6">
              <mat-form-field class="w-full" appearance="outline">
                <mat-label>対象組織</mat-label>
                <mat-select formControlName="organizationId">
                  @for (org of organizationManageService.organizations(); track org.id) {
                    <mat-option [value]="org.id">{{ org.name }}</mat-option>
                  }
                </mat-select>
                @if (form.get('organizationId')?.hasError('required') && form.get('organizationId')?.touched) {
                  <mat-error>対象組織を選択してください</mat-error>
                }
              </mat-form-field>
            </div>
          }

          <div class="mb-6">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>メールアドレス</mat-label>
              <input matInput formControlName="email" type="email" placeholder="user@example.com">
              @if (form.get('email')?.hasError('required') && form.get('email')?.touched) {
                <mat-error>メールアドレスを入力してください</mat-error>
              }
              @if (form.get('email')?.hasError('email') && form.get('email')?.touched) {
                <mat-error>正しいメールアドレスの形式で入力してください</mat-error>
              }
            </mat-form-field>
          </div>

          <div class="mb-6">
            <mat-form-field class="w-full" appearance="outline">
              <mat-label>初期パスワード</mat-label>
              <input matInput formControlName="password" type="password">
              @if (form.get('password')?.hasError('required') && form.get('password')?.touched) {
                <mat-error>パスワードを入力してください</mat-error>
              }
              @if (form.get('password')?.hasError('minlength') && form.get('password')?.touched) {
                <mat-error>パスワードは6文字以上で入力してください</mat-error>
              }
            </mat-form-field>
          </div>

          @if (form.get('role')?.value === 'dep_admin') {
            <div class="mb-6">
              <mat-form-field class="w-full" appearance="outline">
                <mat-label>部門ID</mat-label>
                <mat-select formControlName="departmentId">
                  @for (dept of availableDepartments(); track dept.id) {
                    <mat-option [value]="dept.id">{{ dept.name }}</mat-option>
                  }
                </mat-select>
                @if (form.get('departmentId')?.hasError('required') && form.get('departmentId')?.touched) {
                  <mat-error>部門を選択してください</mat-error>
                }
              </mat-form-field>
            </div>
          }

          @if (error()) {
            <div class="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
              {{ error() }}
            </div>
          }

          <div class="flex justify-end gap-4">
            <button matButton type="button" routerLink="/accounts/list" [disabled]="isSubmitting()">
              キャンセル
            </button>
            <button matButton="tonal" type="submit" [disabled]="form.invalid || isSubmitting()">
              @if (isSubmitting()) {
                作成中...
              } @else {
                追加する
              }
            </button>
          </div>

        </form>
      </div>
    </div>
  `
})
export class AccountCreateComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly accountService = inject(AccountService);
  public readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  public readonly organizationManageService = inject(OrganizationManageService);
  private readonly siteManagementDepartmentService = inject(SiteManagementDepartmentService);
  private readonly destroyRef = inject(DestroyRef);

  public isSubmitting = signal(false);
  public error = signal<string | null>(null);
  public availableDepartments = signal<SiteManagementDepartment[]>([]);

  public form = this.fb.group({
    role: ['', Validators.required],
    organizationId: [''],
    email: [''],
    password: ['', [Validators.required, Validators.minLength(6)]],
    departmentId: ['']
  });

  constructor() {
    this.form.get('role')!.valueChanges.pipe(
      takeUntilDestroyed()
    ).subscribe(() => this.updateValidators());

    effect(() => {
      if (this.authService.userRole() !== 'super_admin') {
        this.availableDepartments.set(this.siteManagementDepartmentService.departments());
      }
    });
  }

  ngOnInit(): void {
    this.updateValidators();
    if (this.authService.userRole() === 'super_admin') {
      this.form.get('organizationId')?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.form.get('departmentId')?.setValue('')),
        switchMap(orgId => {
          if (!orgId) {
            return of<SiteManagementDepartment[]>([]);
          }
          return from(this.siteManagementDepartmentService.getDepartmentsByOrganizationId(orgId)).pipe(
            catchError((err) => {
              console.error(`組織ID [${orgId}] の部門一覧取得に失敗しました`, err);
              this.error.set('部門一覧の取得に失敗しました。再度組織を選択してください。');
              return of<SiteManagementDepartment[]>([]);
            })
          );
        })
      ).subscribe((depts) => {
        this.availableDepartments.set(depts);
      });
    }
  }

  private updateValidators(): void {
    const role = this.form.get('role')?.value;
    
    // reset validators
    this.form.get('email')?.clearValidators();
    this.form.get('departmentId')?.clearValidators();

    if (this.authService.userRole() === 'super_admin') {
      if (role !== 'super_admin') {
        this.form.get('organizationId')?.setValidators([Validators.required]);
      } else {
        this.form.get('organizationId')?.clearValidators();
      }
      this.form.get('organizationId')?.updateValueAndValidity();
    }

    if (role === 'super_admin' || role === 'org_admin') {
      this.form.get('email')?.setValidators([Validators.required, Validators.email]);
    } else if (role === 'dep_admin') {
      this.form.get('email')?.setValidators([Validators.required, Validators.email]);
      this.form.get('departmentId')?.setValidators([Validators.required]);
    }

    this.form.get('email')?.updateValueAndValidity();
    this.form.get('departmentId')?.updateValueAndValidity();
  }

  public async onSubmit(): Promise<void> {
    if (this.form.invalid) return;

    this.isSubmitting.set(true);
    this.error.set(null);

    try {
      const val = this.form.value;
      const role = val.role as UserRole;

      let orgId: string | null | undefined;
      if (role !== 'super_admin') {
        orgId = this.authService.userRole() === 'super_admin' ? val.organizationId! : this.authService.userOrganizationId();
        if (!orgId) throw new Error('テナント情報が取得できません');
      }

      await this.accountService.createAccount({
        email: val.email!,
        password: val.password!,
        role: role,
        ...(orgId ? { organizationId: orgId } : {}),
        departmentId: role === 'dep_admin' ? val.departmentId! : undefined
      });

      this.accountService.reload();
      this.router.navigate(['/accounts/list']);
    } catch (err: any) {
      console.error('アカウント作成エラー', err);
      if (err?.code === 'functions/already-exists') {
        this.error.set('このメールアドレスは既に登録されています。');
      } else {
        this.error.set(err instanceof Error ? err.message : 'アカウントの作成に失敗しました');
      }
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
